'use client';

import { c as _c } from "react/compiler-runtime";
import { InfoCircle, Minus, Plus } from '@byway/icons';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useInteractiveLoader } from '~/components/PartnerLoader/PartnerLoader';
import { useTripOverviewSummary } from '~/components/TripOverview/hooks/useTripOverviewSummary';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { api } from '~/libs/api';
import stepperStyles from '../../NumericStepper/src/NumericStepper.module.css';
import styles from './AccommodationCardNightStepper.module.css';
type AccommodationCardNightStepperProps = {
  nights: number;
  tripId: string;
  stayItemId: number;
};
const DEBOUNCE_MS = 3000;
export const AccommodationCardNightStepper = t0 => {
  const $ = _c(75);
  const {
    nights: initialNights,
    tripId,
    stayItemId
  } = t0;
  const router = useRouter();
  const utils = api.useUtils();
  const t = useTranslations("journey");
  const tFeedback = useTranslations("content.feedback");
  const [nights, setNights] = useState(initialNights);
  const [isCommitting, setIsCommitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const {
    isDateUnset,
    canEditTripSelections,
    isLiveChecking
  } = useTripOverviewSummary();
  const {
    startLoader,
    completeProgressBar
  } = useInteractiveLoader();
  const isTripUpdating = useIsTripUpdating();
  const isLocked = isDateUnset || !canEditTripSelections || isLiveChecking || isTripUpdating && !isCommitting;
  let t1;
  if ($[0] !== initialNights || $[1] !== router || $[2] !== tFeedback || $[3] !== tripId || $[4] !== utils) {
    t1 = (result, variables) => {
      if (result?.success) {
        setErrorMessage(null);
        router.refresh();
        utils.trip.getOverview.invalidate({
          tripId
        });
        utils.trip.getPrice.invalidate({
          tripId
        });
      } else {
        setNights(initialNights);
        setErrorMessage(tFeedback("nights_update_unavailable", {
          nights: variables.newDurationNights
        }));
      }
    };
    $[0] = initialNights;
    $[1] = router;
    $[2] = tFeedback;
    $[3] = tripId;
    $[4] = utils;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== initialNights || $[7] !== tFeedback) {
    t2 = () => {
      setNights(initialNights);
      setErrorMessage(tFeedback("error_generic"));
    };
    $[6] = initialNights;
    $[7] = tFeedback;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== completeProgressBar) {
    t3 = () => {
      setIsCommitting(false);
      completeProgressBar();
    };
    $[9] = completeProgressBar;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3) {
    t4 = {
      onSuccess: t1,
      onError: t2,
      onSettled: t3
    };
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const {
    mutate,
    isPending
  } = api.trip.changeDuration.useMutation(t4);
  let t5;
  if ($[15] !== mutate || $[16] !== stayItemId || $[17] !== tripId) {
    t5 = newNights => {
      mutate({
        journeyId: tripId,
        stayItemId,
        newDurationNights: newNights
      });
    };
    $[15] = mutate;
    $[16] = stayItemId;
    $[17] = tripId;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const debouncedMutate = useDebouncedCallback(t5, DEBOUNCE_MS);
  let t6;
  let t7;
  if ($[19] !== debouncedMutate) {
    t6 = () => () => {
      debouncedMutate.flush();
    };
    t7 = [debouncedMutate];
    $[19] = debouncedMutate;
    $[20] = t6;
    $[21] = t7;
  } else {
    t6 = $[20];
    t7 = $[21];
  }
  useEffect(t6, t7);
  let t8;
  if ($[22] !== debouncedMutate || $[23] !== startLoader) {
    t8 = next => {
      setErrorMessage(null);
      setNights(next);
      setIsCommitting(true);
      startLoader({
        showProgressBar: false
      });
      debouncedMutate(next);
    };
    $[22] = debouncedMutate;
    $[23] = startLoader;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const handleStep = t8;
  const t9 = errorMessage && styles.wrapperError;
  let t10;
  if ($[25] !== t9) {
    t10 = clsx(styles.wrapper, t9);
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t) {
    t11 = t("itinerary_night_label");
    $[27] = t;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t11) {
    t12 = <span className={styles.nightsLabel}>{t11}</span>;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = clsx(stepperStyles.button, styles.stepButton);
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const t14 = isPending || isLocked || nights <= 1;
  let t15;
  if ($[32] !== handleStep || $[33] !== nights) {
    t15 = () => handleStep(Math.max(1, nights - 1));
    $[32] = handleStep;
    $[33] = nights;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t) {
    t16 = t("itinerary_decrease_nights");
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Minus decorative={true} strokeWidth={2} />;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t14 || $[39] !== t15 || $[40] !== t16) {
    t18 = <button type="button" className={t13} data-ua-click="adjust-length-stay" disabled={t14} onClick={t15} aria-label={t16}>{t17}</button>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== nights || $[43] !== t) {
    t19 = t("nights_count", {
      count: nights
    });
    $[42] = nights;
    $[43] = t;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== nights || $[46] !== t19) {
    t20 = <output aria-live="polite" aria-label={t19} className={stepperStyles.input}>{nights}</output>;
    $[45] = nights;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = clsx(stepperStyles.button, styles.stepButton);
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const t22 = isPending || isLocked;
  let t23;
  if ($[49] !== handleStep || $[50] !== nights) {
    t23 = () => handleStep(nights + 1);
    $[49] = handleStep;
    $[50] = nights;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t) {
    t24 = t("itinerary_increase_nights");
    $[52] = t;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Plus decorative={true} strokeWidth={2} />;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t22 || $[56] !== t23 || $[57] !== t24) {
    t26 = <button type="button" className={t21} data-ua-click="adjust-length-stay" disabled={t22} onClick={t23} aria-label={t24}>{t25}</button>;
    $[55] = t22;
    $[56] = t23;
    $[57] = t24;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t18 || $[60] !== t20 || $[61] !== t26) {
    t27 = <div className={stepperStyles.wrapper}>{t18}{t20}{t26}</div>;
    $[59] = t18;
    $[60] = t20;
    $[61] = t26;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t12 || $[64] !== t27) {
    t28 = <div className={styles.row}>{t12}{t27}</div>;
    $[63] = t12;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== errorMessage) {
    t29 = errorMessage && <div className={styles.errorRow}><InfoCircle decorative={true} size="small" /><p className={styles.errorText}>{errorMessage}</p></div>;
    $[66] = errorMessage;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== isCommitting) {
    t30 = isCommitting && <div className={styles.loadingBar} aria-hidden={true} />;
    $[68] = isCommitting;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== t10 || $[71] !== t28 || $[72] !== t29 || $[73] !== t30) {
    t31 = <div className={t10}>{t28}{t29}{t30}</div>;
    $[70] = t10;
    $[71] = t28;
    $[72] = t29;
    $[73] = t30;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  return t31;
};